import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Flex, Typography } from 'antd';
import type { Dayjs } from 'dayjs';
import { useMachineDetailsRobotSchedule } from '../../context/MachineDetailsRobotScheduleContext';
import { getSelectedWeekFromRange, isRangeSelection } from '../../utils/period';
import {
  ScheduleHeaderFilterContainer,
  ScheduleHeaderStatusContainer,
  ScheduleRangePicker,
  ScheduleRangePickerContainer,
} from './ScheduleHeader.styles';
import { ScheduleHeaderSyncStatusMemoized } from './ScheduleHeaderSyncStatus';
import { kccThemeConstants } from 'config/theme/kccTheme/kccTheme';
import { SvgIcon } from 'lib/components/SvgIcon/SvgIcon';
import { DateTime } from 'lib/utils/date-handling/DateTime';
import { DATE_FORMAT_PATTERN } from 'lib/utils/date-handling/DateTime.types';

export const ScheduleHeader = (): JSX.Element => {
  const { t, i18n } = useTranslation();
  const {
    period,
    weekNumber,
    isRobotOnline,
    isRobotVersionSupported,
    isScheduleMapsLoading,
    handlePeriodChange,
    handlePreviousWeek,
    handleNextWeek,
    handleToday,
    handleCreateScheduleClick,
  } = useMachineDetailsRobotSchedule();

  const isCreateScheduleDisabled = !isRobotOnline || !isRobotVersionSupported;

  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const activeRangeRef = useRef<'start' | 'end'>('start');

  const handleCalendarChange = useCallback(
    (
      dates: [Dayjs | null, Dayjs | null],
      _dateStrings: [string, string],
      info: {
        range?: 'start' | 'end';
      }
    ): void => {
      if (!isRangeSelection(info) || !Array.isArray(dates)) {
        return;
      }

      activeRangeRef.current = info.range;

      const selectedWeek = getSelectedWeekFromRange(dates, info.range);

      if (!selectedWeek) {
        return;
      }

      handlePeriodChange([selectedWeek, selectedWeek]);
      setIsPickerOpen(false);
    },
    [handlePeriodChange]
  );

  const handleRangeChange = useCallback(
    (dates: [Dayjs | null, Dayjs | null] | null): void => {
      if (!dates || !Array.isArray(dates)) {
        return;
      }

      const selectedWeek = getSelectedWeekFromRange(dates, activeRangeRef.current);

      if (!selectedWeek) {
        return;
      }

      handlePeriodChange([selectedWeek, selectedWeek]);
    },
    [handlePeriodChange]
  );

  return (
    <Flex vertical gap={kccThemeConstants.SPACING.LG}>
      <ScheduleHeaderStatusContainer justify="flex-end" align="flex-end">
        {isRobotVersionSupported && <ScheduleHeaderSyncStatusMemoized disabled={isCreateScheduleDisabled} />}

        <Button
          type="primary"
          icon={<SvgIcon name="add" />}
          onClick={handleCreateScheduleClick}
          disabled={isCreateScheduleDisabled}
          loading={isScheduleMapsLoading}
        >
          {t('common.createSchedule')}
        </Button>
      </ScheduleHeaderStatusContainer>

      <ScheduleHeaderFilterContainer gap={kccThemeConstants.SPACING.BASE} align="center">
        <Typography.Text
          style={{
            fontWeight: kccThemeConstants.TYPOGRAPHY.FONT_WEIGHT.EXTRA_BOLD,
            lineHeight: '20px',
            textTransform: 'capitalize',
          }}
        >
          {t('common.week', { weekNumber })}
        </Typography.Text>

        <ScheduleRangePickerContainer gap={kccThemeConstants.SPACING.BASE} align="center">
          <Button
            type="link"
            icon={<SvgIcon name="rightChevron" />}
            onClick={handlePreviousWeek}
            style={{ width: kccThemeConstants.SIZE.S250 }}
            aria-label={t('common.previousWeek')}
            title={t('common.previousWeek')}
          />

          <ScheduleRangePicker
            allowClear={false}
            className="kcc-theme-range-picker"
            open={isPickerOpen}
            onOpenChange={setIsPickerOpen}
            value={[period.startDate, period.endDate]}
            onCalendarChange={handleCalendarChange}
            onChange={handleRangeChange}
            popupClassName="kcc-theme-date-picker-popup"
            picker="week"
            format={(date): string =>
              DateTime.formatDateByLocale(i18n.language, date.toISOString(), DATE_FORMAT_PATTERN.DATE)
            }
          />

          <Button
            type="link"
            icon={<SvgIcon name="rightChevron" />}
            onClick={handleNextWeek}
            aria-label={t('common.nextWeek')}
            title={t('common.nextWeek')}
            style={{ width: kccThemeConstants.SIZE.S250 }}
          />
        </ScheduleRangePickerContainer>

        <Button type="default" onClick={handleToday} style={{ minWidth: '100px' }}>
          {t('common.today')}
        </Button>
      </ScheduleHeaderFilterContainer>
    </Flex>
  );
};
